/* eslint-disable class-methods-use-this */
import { AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import { paramsAllRequest } from 'SharedDir/backend/api/default-params';
import { TTP_PERSON_API, physicalPersonUrl } from 'SharedDir/backend/endpoints/physical-person';
import { StatusResponse } from 'SharedDir/declarations/endpoint';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import {
  ChangeHouseholdInfo,
  ChangeHouseholdRoleInfo,
  ExcludeFromRegistryInfo,
  IncludeToRegistry,
  NomadicStatusUpdate,
  PersonBare,
  PersonCreate,
  PersonFull,
  PersonRegistryUpdate,
  PersonUpdate,
} from 'SharedDir/declarations/physical-person';
import { StatusMessage } from 'SharedDir/declarations/shared';
import { EventHistoryBare } from 'SharedDir/declarations/ttp-event-history';
import { TTPRegistries } from 'SharedDir/declarations/ttp-registries';
import { ZAGS } from 'SharedDir/declarations/zags';

import PersonRegistryHistoryItem = TTPRegistries.PersonRegistryHistoryItem;

export class PhysicalPersonApiClass extends SimpleApi<
  PersonFull,
  PersonCreate,
  PersonUpdate,
  PersonBare
> {
  constructor() {
    super(physicalPersonUrl);
  }

  /**
   * Смена домохохяйства физическому лицу в реестре
   *
   * @param {string | number} id
   * @param {ChangeHouseholdInfo} info
   * @returns {Promise<PersonFull>}
   */
  async changeHouseHold(id: string | number, info: ChangeHouseholdInfo) {
    const { data } = (await http({
      ...TTP_PERSON_API.CHANGE_HOUSEHOLD(id),
      data: info,
    })) as AxiosResponse<PersonFull>;
    return data;
  }

  /**
   * Смена роли физического лица в домохозяйстве
   *
   * @param {string | number} id
   * @param info
   * @returns {Promise<PersonFull>}
   */
  async changeHouseHoldRole(id: string | number, info: ChangeHouseholdRoleInfo) {
    const { data } = (await http({
      ...TTP_PERSON_API.CHANGE_HOUSEHOLD_ROLE(id),
      data: info,
    })) as AxiosResponse<PersonFull>;
    return data;
  }

  /**
   * Исключение физического лица из реестра ТТП по причине смерти, решению комиссии или добровольно
   *
   * @param {string | number} id
   * @param info
   * @returns {Promise<PersonFull>}
   */
  async exclude(id: string | number, info: ExcludeFromRegistryInfo) {
    const { data } = (await http({
      ...TTP_PERSON_API.EXCLUDE(id),
      data: info,
    })) as AxiosResponse<PersonFull>;
    return data;
  }

  async include(id: string | number, info: IncludeToRegistry) {
    const { data } = (await http({
      ...TTP_PERSON_API.INCLUDE(id),
      data: info,
    })) as AxiosResponse<PersonFull>;

    return data;
  }

  /**
   * Востановление физического лица в реестр ТТП
   *
   * @param {string | number} id
   * @returns {Promise<PersonFull>}
   */
  async restore(id: string | number) {
    const { data } = (await http({
      ...TTP_PERSON_API.RESTORE(id),
    })) as AxiosResponse<PersonFull>;
    return data;
  }

  /**
   * Воскркешение ФЛ, затирает дату смерти в ФЛ и убирает документ-свидетельство о смерти.
   *
   * @deprecated
   * @param {string | number} id
   * @returns {Promise<PersonFull>}
   */
  async resurrect(id: string | number) {
    const { data } = (await http({
      ...TTP_PERSON_API.RESURRECT(id),
    })) as AxiosResponse<PersonFull>;
    return data;
  }

  /**
   * Ручной запрос интеграции для конкретного физ. лица
   * @param personId
   * @param integrationCinfigId
   */
  async callIntegration(personId: number | string, integrationCinfigId: number) {
    const response = (await http(
      TTP_PERSON_API.CALL_INTEGRATION(personId, integrationCinfigId)
    )) as AxiosResponse<StatusResponse>;
  }

  /**
   * Ручной запуск запроса справки Минобороны по всем совершеннолетним членам семьи
   */
  async callFamilyCitizenStatusIntegration(personId: number | string) {
    const { data } = (await http(
      TTP_PERSON_API.CALL_FAMILY_CITIZEN_STATUS_INTEGRATION(personId)
    )) as AxiosResponse<StatusResponse>;
    return data;
  }

  async getHistory(params) {
    const {
      data: { data },
    } = (await http({
      ...TTP_PERSON_API.GET_REGISTRY_HISTORY,
      params: { ...paramsAllRequest, sortBy: 'foundationDate', descending: true, ...params },
    })) as AxiosResponse<Paginated<TTPRegistries.PersonRegistryHistoryItem>>;
    return data;
  }

  /**
   * Обновление реестровой записи физического лица
   *
   * @param id
   * @param payload
   */
  async getRegistryRecord(id: string | number) {
    const { data } = (await http(
      TTP_PERSON_API.GET_REGISTRY(id)
    )) as AxiosResponse<PersonRegistryHistoryItem>;
    return data;
  }

  /**
   * Обновление реестровой записи физического лица
   *
   * @param id
   * @param payload
   */
  async updateRegistryRecord(id: string | number, payload: PersonRegistryHistoryItem) {
    const { data } = (await http({
      ...TTP_PERSON_API.UPDATE_REGISTRY(id),
      data: payload,
    })) as AxiosResponse<PersonRegistryHistoryItem>;
    return data;
  }

  /**
   * Обновление реестровой записи физического лица
   *
   * @param registryId
   * @param registryInfoId
   * @param payload
   */
  async updateRegistryInfo(
    registryId: string | number,
    registryInfoId: string | number,
    payload:
      | TTPRegistries.TtpRegistryData
      | TTPRegistries.TojRegistryData
      | TTPRegistries.ThdRegistryData
  ) {
    const { data } = (await http({
      ...TTP_PERSON_API.UPDATE_REGISTRY_DATA(registryId, registryInfoId),
      data: payload,
    })) as AxiosResponse<PersonRegistryHistoryItem>;
    return data;
  }

  /**
   * Обновление статуса кочевья (только статус, дата, акты; МО не изменяется)
   *
   * @param id - ID физического лица
   * @param data - Данные статуса кочевья
   * @returns {Promise<PersonFull>}
   */
  async updateNomadicStatus(id: string | number, data: NomadicStatusUpdate): Promise<PersonFull> {
    const { data: result } = (await http({
      ...TTP_PERSON_API.UPDATE_NOMADIC_STATUS(id),
      data,
    })) as AxiosResponse<PersonFull>;
    return result;
  }
}
